import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Mock credentials for different user roles
  const mockCredentials = {
    admin: { email: 'admin@consultflow.com', password: 'admin123' },
    technician: { email: 'tecnico@consultflow.com', password: 'tecnico123' },
    client: { email: 'cliente@consultflow.com', password: 'cliente123' }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'E-mail inválido';
    }

    if (!formData?.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      const { email, password } = formData;
      
      // Check credentials and determine role
      let userRole = null;
      let isValidCredentials = false;

      Object.entries(mockCredentials)?.forEach(([role, credentials]) => {
        if (email === credentials?.email && password === credentials?.password) {
          userRole = role;
          isValidCredentials = true;
        }
      });

      if (isValidCredentials) {
        // Store user session
        localStorage.setItem('userRole', userRole);
        localStorage.setItem('userEmail', email);
        localStorage.setItem('rememberMe', formData?.rememberMe);

        // Navigate based on role
        switch (userRole) {
          case 'admin': navigate('/admin-dashboard');
            break;
          case 'technician': navigate('/technician-mobile');
            break;
          case 'client': navigate('/client-portal');
            break;
          default:
            navigate('/admin-dashboard');
        }
      } else {
        setErrors({
          general: 'E-mail ou senha incorretos. Verifique suas credenciais e tente novamente.'
        });
      }
      
      setIsLoading(false);
    }, 1500);
  };

  const handleForgotPassword = () => {
    // In a real app, this would navigate to password reset
    alert('Funcionalidade de recuperação de senha será implementada em breve.');
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card border border-border rounded-lg shadow-professional p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="Briefcase" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            Bem-vindo ao ConsultFlow
          </h1>
          <p className="text-muted-foreground">
            Faça login para acessar sua conta
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* General Error */}
          {errors?.general && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <p className="text-sm text-error">{errors?.general}</p>
              </div>
            </div>
          )}

          {/* Email Input */}
          <Input
            label="E-mail"
            type="email"
            name="email"
            placeholder="seu@email.com"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
            disabled={isLoading}
          />

          {/* Password Input */}
          <Input
            label="Senha"
            type="password"
            name="password"
            placeholder="Digite sua senha"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
            disabled={isLoading}
          />

          {/* Remember Me */}
          <div className="flex items-center justify-between">
            <Checkbox
              label="Lembrar-me"
              name="rememberMe"
              checked={formData?.rememberMe}
              onChange={handleInputChange}
              disabled={isLoading}
            />
            
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm text-primary hover:text-primary/80 transition-colors"
              disabled={isLoading}
            >
              Esqueci minha senha
            </button>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            className="mt-6"
          >
            {isLoading ? 'Entrando...' : 'Entrar'}
          </Button>
        </form>

        {/* Role Indicators */}
        <div className="mt-6 pt-6 border-t border-border">
          <p className="text-xs text-muted-foreground text-center mb-3">
            Tipos de acesso disponíveis:
          </p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-muted rounded-lg">
              <Icon name="Shield" size={16} className="mx-auto mb-1 text-primary" />
              <p className="text-xs font-medium text-foreground">Admin</p>
            </div>
            <div className="p-2 bg-muted rounded-lg">
              <Icon name="Wrench" size={16} className="mx-auto mb-1 text-success" />
              <p className="text-xs font-medium text-foreground">Técnico</p>
            </div>
            <div className="p-2 bg-muted rounded-lg">
              <Icon name="User" size={16} className="mx-auto mb-1 text-accent" />
              <p className="text-xs font-medium text-foreground">Cliente</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;